import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { classifyApiError } from '../services/http.js';
import { ToastViewport } from '../components/Toast/ToastViewport.jsx';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
};

const DEFAULT_DURATION_MS = { error: 9000, success: 4000, info: 6000 };

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const showToast = useCallback(({ type = 'info', message, detail, requestId, actionLabel, onAction, duration } = {}) => {
    const id = crypto.randomUUID();
    const ms = duration ?? DEFAULT_DURATION_MS[type] ?? DEFAULT_DURATION_MS.info;
    setToasts((prev) => [...prev, { id, type, message, detail, requestId, actionLabel, onAction }]);
    if (ms > 0) {
      timers.current[id] = setTimeout(() => dismissToast(id), ms);
    }
    return id;
  }, [dismissToast]);

  // Traduz uma resposta HTTP não-ok (ou uma exceção de rede, passando res=null)
  // em um toast, reaproveitando classifyApiError (services/http.js) para
  // diferenciar indisponibilidade (rede/servidor — ganha retry) de erro de
  // negócio (mensagem específica da API, sem retry).
  const showApiErrorToast = useCallback((res, data, fallbackMessage, { onRetry } = {}) => {
    const { type: kind, message } = classifyApiError(res, data, fallbackMessage);
    const requestId = (typeof res?.headers?.get === 'function' ? res.headers.get('x-request-id') : null)
      || data?.error?.request_id
      || null;

    // P23C: um 403 de recurso bloqueado pelo plano (Sínodo, limite de
    // Leitura/Trilhas/Caderno, persona não liberada etc.) ganha um CTA para
    // a página de planos em vez do "Tentar novamente" (repetir a mesma
    // chamada nunca resolveria um bloqueio de plano).
    if (kind === 'upgrade') {
      return showToast({
        type: 'error',
        message,
        requestId,
        actionLabel: 'Ver planos',
        onAction: () => { window.location.assign('/planos'); },
      });
    }

    return showToast({
      type: 'error',
      message,
      requestId,
      actionLabel: kind !== 'business' && onRetry ? 'Tentar novamente' : undefined,
      onAction: onRetry,
    });
  }, [showToast]);

  const value = { showToast, dismissToast, showApiErrorToast };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};
